import {
  CLIENT_STATUS_LABELS,
  type ClientStatus,
} from '@lib/enums/client-status.enum'
import { CLIENT_TYPE_LABELS, type ClientType } from '@lib/enums/client-type.enum'
import {
  SITE_MAP_STATUS_LABELS,
  type SiteMapStatus,
} from '@lib/enums/site-map-status.enum'
import {
  DOCUMENT_KIND_LABELS,
  type DocumentKind,
} from '@lib/enums/document-kind.enum'
import {
  PROPOSAL_VERSION_STATUS_LABELS,
  type ProposalVersionStatus,
} from '@lib/enums/proposal-version-status.enum'
import { StatusPill, type PillTone } from '@/components/StatusPill'

/**
 * Status pills for the client domain. Hues are semantic, not decorative:
 * green means "this is live work", gray means inert, amber means unfinished.
 * Labels come from the shared enum files so the API and UI cannot disagree
 * about what a value is called.
 */
const CLIENT_STATUS_TONES: Record<ClientStatus, PillTone> = {
  PROSPECT: 'blue',
  ACTIVE: 'green',
  INACTIVE: 'gray',
}

export function ClientStatusBadge({ status }: { status: ClientStatus }) {
  return (
    <StatusPill tone={CLIENT_STATUS_TONES[status]}>
      {CLIENT_STATUS_LABELS[status]}
    </StatusPill>
  )
}

const CLIENT_TYPE_TONES: Record<ClientType, PillTone> = {
  COMMERCIAL: 'purple',
  RESIDENTIAL: 'orange',
}

export function ClientTypeBadge({ type }: { type: ClientType }) {
  return (
    <StatusPill tone={CLIENT_TYPE_TONES[type]}>
      {CLIENT_TYPE_LABELS[type]}
    </StatusPill>
  )
}

const SITE_MAP_TONES: Record<SiteMapStatus, PillTone> = {
  NONE: 'gray',
  IN_PROGRESS: 'yellow',
  COMPLETE: 'green',
}

export function SiteMapStatusBadge({ status }: { status: SiteMapStatus }) {
  return (
    <StatusPill tone={SITE_MAP_TONES[status]}>
      {SITE_MAP_STATUS_LABELS[status]}
    </StatusPill>
  )
}

/**
 * Proposal version status.
 *
 * Blue for Sent (it is live, awaiting an answer), green for Accepted, red for
 * Declined, amber for Expired (it needs attention), gray for Draft and
 * Superseded — both inert, one not yet out and one no longer current.
 */
const PROPOSAL_STATUS_TONES: Record<ProposalVersionStatus, PillTone> = {
  DRAFT: 'gray',
  SENT: 'blue',
  SUPERSEDED: 'gray',
  EXPIRED: 'yellow',
  ACCEPTED: 'green',
  DECLINED: 'red',
}

export function ProposalStatusBadge({
  status,
}: {
  status: ProposalVersionStatus
}) {
  return (
    <StatusPill tone={PROPOSAL_STATUS_TONES[status]}>
      {PROPOSAL_VERSION_STATUS_LABELS[status]}
    </StatusPill>
  )
}

const DOCUMENT_KIND_TONES: Record<DocumentKind, PillTone> = {
  SITE_MAP: 'blue',
  EXAMPLE_WORK: 'purple',
  PHOTO: 'gray',
  CONTRACT: 'green',
  // Neutral: a logo is chrome, not a category of client work.
  LOGO: 'gray',
  OTHER: 'gray',
}

export function DocumentKindBadge({ kind }: { kind: DocumentKind }) {
  return (
    <StatusPill tone={DOCUMENT_KIND_TONES[kind]}>
      {DOCUMENT_KIND_LABELS[kind]}
    </StatusPill>
  )
}
